import {
  LucideIcon,
  Sparkles,
  FileText,
  FolderOpen,
  Table2,
  Zap,
  Shield,
  Plug,
  Wand2,
  Brain,
  Clock,
  Target,
  Lock,
  Users,
  CheckCircle,
  BarChart3,
  Layers,
  Database,
  Settings,
  Search,
  Globe,
  RefreshCw,
  Key,
  FileCheck,
  Share2,
  Activity,
  Download,
  CalendarClock,
  ListChecks,
  MessageSquare,
} from 'lucide-react'

/** Product UI fragment drawn for a use case on the platform page. */
export type UiFragmentKind =
  | 'prompt'
  | 'form'
  | 'chart'
  | 'locked'
  | 'flow'
  | 'assets'
  | 'audit'
  | 'taskpane'
  | 'sheet'
  | 'mail'
  | 'roles'

/**
 * Page composition for a platform page. Each page has its own layout
 * (see `pages/platform/layouts/`).
 */
export type PlatformLayout =
  | 'dark-product'
  | 'creative'
  | 'asset-listing'
  | 'giant-word'
  | 'statement'
  | 'numbers'
  | 'sticky-split'
  | 'heatmap'

export interface PlatformFaq {
  question: string
  answer: string
}

export interface PlatformFeature {
  slug: string
  /** Which page layout renders this feature. */
  layout: PlatformLayout
  icon: LucideIcon
  color: string
  colorDark: string
  metaTitle: string
  metaDescription: string
  label: string
  /** Short name used in navigation and the hero highlight box. */
  shortLabel: string
  headline: string
  /** The headline rewritten as the hero's two punchy lines. */
  heroLines: [string, string]
  subheadline: string
  heroStats: { value: string; label: string }[]
  heroImage?: string
  heroImageAlt?: string
  valueProposition: {
    title: string
    description: string
  }
  capabilities: {
    icon: LucideIcon
    title: string
    description: string
  }[]
  benefits: {
    icon: LucideIcon
    title: string
    description: string
    stat?: string
  }[]
  useCases: {
    title: string
    description: string
    industry: string
    /** UI fragment that illustrates this use case. */
    fragment: UiFragmentKind
    /** Prompt and answer for the 'prompt' fragment. */
    demo?: { ask: string; answer: string }
  }[]
  /** FAQ, answered from the copy on this page. */
  faq: PlatformFaq[]
  ctaTitle: string
  ctaDescription: string
}

export const platformFeatures: Record<string, PlatformFeature> = {
  'ai-generation': {
    slug: 'ai-generation',
    layout: 'dark-product',
    icon: Sparkles,
    color: 'from-violet-500 to-purple-600',
    colorDark: 'from-violet-400 to-purple-500',
    metaTitle: 'AI Document Generation | Templatean',
    metaDescription: 'Generate professional documents in minutes with AI. Smart content creation that understands your brand, audience, and goals.',
    label: 'AI Document Generation',
    shortLabel: 'AI Generation',
    headline: 'Write documents 10x faster with AI that understands your business',
    heroLines: ['Documents 10x faster.', 'In your brand voice.'],
    subheadline: 'Generate first drafts, personalize content, and maintain consistency—all powered by AI that learns your brand voice.',
    heroStats: [
      { value: '10x', label: 'Faster document creation' },
      { value: '95%', label: 'Brand consistency' },
      { value: '80%', label: 'Time saved on drafts' },
    ],
    heroImage: '/images/screenshots/workflow-builder.png',
    heroImageAlt: 'AI Studio Workflow Builder - Create custom AI agents and automated workflows',
    valueProposition: {
      title: 'AI that writes like your best team member',
      description: 'Our AI doesn\'t just generate generic content—it learns your company\'s tone, terminology, and standards to produce documents that feel authentically yours.',
    },
    capabilities: [
      {
        icon: Wand2,
        title: 'Smart First Drafts',
        description: 'Generate complete document drafts from brief inputs. Proposals, reports, contracts—ready in minutes, not hours.',
      },
      {
        icon: Brain,
        title: 'Brand Voice Learning',
        description: 'AI that adapts to your company\'s unique voice and style, ensuring every document feels authentic.',
      },
      {
        icon: Target,
        title: 'Audience Personalization',
        description: 'Automatically tailor content for different audiences while maintaining your core message and brand.',
      },
      {
        icon: RefreshCw,
        title: 'Iterative Refinement',
        description: 'Collaborate with AI to refine and improve content through natural conversation.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Hours saved per document',
        description: 'Reduce document creation time from hours to minutes with AI-generated first drafts.',
        stat: '4+ hours',
      },
      {
        icon: CheckCircle,
        title: 'Consistent quality',
        description: 'Every document meets your standards with built-in quality checks and brand compliance.',
        stat: '95%',
      },
      {
        icon: Users,
        title: 'Scale your team',
        description: 'Handle more work without hiring more people. AI multiplies your team\'s output.',
        stat: '3x',
      },
      {
        icon: Target,
        title: 'Better outcomes',
        description: 'AI-optimized content leads to higher engagement and better results.',
        stat: '+25%',
      },
    ],
    useCases: [
      {
        title: 'Proposal Generation',
        description: 'Generate customized proposals in minutes by combining your winning templates with AI-personalized content.',
        industry: 'Professional Services',
        fragment: 'prompt',
        demo: {
          ask: 'Draft a proposal for {{client.name}} from our winning template',
          answer: 'Executive summary · Proposed approach · Timeline · Investment. Personalized for {{client.name}}, in your brand voice.',
        },
      },
      {
        title: 'Contract Drafting',
        description: 'Create accurate first drafts with appropriate clauses and language for any deal type.',
        industry: 'Legal',
        fragment: 'form',
        demo: {
          ask: 'Draft a services agreement for {{party.name}} with our standard clauses',
          answer: 'Parties · Scope · Term · Fees · Governing law. Standard clauses kept as approved; deal terms filled in.',
        },
      },
      {
        title: 'Report Writing',
        description: 'Transform data into compelling narratives with AI that understands your analytical style.',
        industry: 'Finance',
        fragment: 'chart',
        demo: {
          ask: 'Turn the Q3 figures into a two-page summary for the board',
          answer: 'Headline results · What moved and why · Risks · Next quarter. Written in your analytical style.',
        },
      },
    ],
    faq: [
      {
        question: 'Does the AI write in our voice?',
        answer: 'Yes. It learns your company\'s tone, terminology and standards, so every document feels authentically yours.',
      },
      {
        question: 'What can it draft?',
        answer: 'Proposals, reports, contracts and more. Complete first drafts from brief inputs, ready in minutes, not hours.',
      },
      {
        question: 'Can we refine what it writes?',
        answer: 'Yes. Collaborate with the AI and refine and improve content through natural conversation.',
      },
      {
        question: 'Can content be tailored to different audiences?',
        answer: 'Content is automatically tailored for each audience while keeping your core message and brand.',
      },
    ],
    ctaTitle: 'See AI generation in action',
    ctaDescription: 'Watch how teams create professional documents 10x faster with Templatean AI.',
  },

  templates: {
    slug: 'templates',
    layout: 'creative',
    icon: FileText,
    color: 'from-blue-500 to-cyan-600',
    colorDark: 'from-blue-400 to-cyan-500',
    metaTitle: 'Template Management | Templatean',
    metaDescription: 'Centralized template management with dynamic fields, version control, and team collaboration. Your single source of truth for all documents.',
    label: 'Template Management',
    shortLabel: 'Templates',
    headline: 'One source of truth for all your document templates',
    heroLines: ['One source of truth', 'for every template.'],
    subheadline: 'Centralized template management that keeps everyone on the same page. Create once, use everywhere, update instantly.',
    heroStats: [
      { value: '100%', label: 'Template consistency' },
      { value: '0', label: 'Version conflicts' },
      { value: '60%', label: 'Faster document creation' },
    ],
    valueProposition: {
      title: 'Templates that scale with your business',
      description: 'Stop emailing templates around. Templatean gives your entire organization instant access to approved, up-to-date templates that anyone can use but only authorized users can modify.',
    },
    capabilities: [
      {
        icon: Layers,
        title: 'Dynamic Fields',
        description: 'Smart placeholders that auto-populate from HubSpot, a connected data source or manual input. No more copy-paste errors.',
      },
      {
        icon: RefreshCw,
        title: 'Version Control',
        description: 'Full history of every template change. Roll back anytime. Always know who changed what.',
      },
      {
        icon: Lock,
        title: 'Access Controls',
        description: 'Define who can view, use, edit, and approve templates. Protect critical content.',
      },
      {
        icon: Settings,
        title: 'Locked Elements',
        description: 'Lock specific sections while allowing customization in others. Balance flexibility with control.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Instant updates',
        description: 'Update a template once, and everyone gets the latest version automatically.',
        stat: 'Real-time',
      },
      {
        icon: CheckCircle,
        title: 'Zero errors',
        description: 'Dynamic fields and validation eliminate manual data entry mistakes.',
        stat: '0%',
      },
      {
        icon: Users,
        title: 'Team adoption',
        description: 'Self-service templates mean less bottlenecks and faster work.',
        stat: '85%',
      },
      {
        icon: Shield,
        title: 'Compliance ready',
        description: 'Audit trails and approval workflows ensure every document is compliant.',
        stat: '100%',
      },
    ],
    useCases: [
      {
        title: 'Sales Proposals',
        description: 'Empower sales teams with pre-approved templates that pull deal and contact data directly from HubSpot.',
        industry: 'Sales',
        fragment: 'form',
      },
      {
        title: 'Legal Contracts',
        description: 'Standardize contract language while allowing controlled customization for specific deals.',
        industry: 'Legal',
        fragment: 'locked',
      },
      {
        title: 'HR Documents',
        description: 'Generate offer letters, policies and employee documents from your HR data, through a REST or database connector.',
        industry: 'Human Resources',
        fragment: 'form',
      },
    ],
    faq: [
      {
        question: 'Who can change a template?',
        answer: 'Anyone can use approved templates; only authorized users can modify them. You define who can view, use, edit and approve.',
      },
      {
        question: 'What happens when a template changes?',
        answer: 'Everyone gets the latest version automatically, and the full history is kept so you can roll back anytime.',
      },
      {
        question: 'Where does dynamic field data come from?',
        answer: 'Smart placeholders auto-populate from HubSpot, SharePoint, Excel Online, a SQL database, a custom REST API or manual input. No more copy-paste errors.',
      },
      {
        question: 'Can parts of a template be locked?',
        answer: 'Yes. Lock specific sections while allowing customization in others.',
      },
    ],
    ctaTitle: 'Take control of your templates',
    ctaDescription: 'See how centralized template management transforms document workflows.',
  },

  dam: {
    slug: 'dam',
    layout: 'asset-listing',
    icon: FolderOpen,
    color: 'from-emerald-500 to-green-600',
    colorDark: 'from-emerald-400 to-green-500',
    metaTitle: 'Brand Asset Management (DAM) | Templatean',
    metaDescription: 'Digital asset management built for brand consistency. Store, organize, and distribute brand assets with intelligent tagging and access controls.',
    label: 'Brand Asset Management',
    shortLabel: 'Brand Assets',
    headline: 'Your brand assets, perfectly organized and always accessible',
    heroLines: ['Every brand asset.', 'Always current.'],
    subheadline: 'A single home for every logo, image, font, and guideline. Find what you need instantly. Know it\'s always current.',
    heroStats: [
      { value: '98%', label: 'Brand compliance' },
      { value: '5 sec', label: 'Average asset search' },
      { value: '1', label: 'Source of truth' },
    ],
    valueProposition: {
      title: 'End the hunt for brand assets',
      description: 'No more digging through shared drives or asking marketing for the "latest" logo. Templatean DAM gives everyone instant access to approved brand assets—with confidence they\'re always current.',
    },
    capabilities: [
      {
        icon: Search,
        title: 'Smart Search',
        description: 'AI-powered search with auto-tagging. Find any asset by keyword, color, type, or visual similarity.',
      },
      {
        icon: Layers,
        title: 'Asset Organization',
        description: 'Collections, folders, and tags keep assets organized. Multiple taxonomies for different team needs.',
      },
      {
        icon: Lock,
        title: 'Usage Rights',
        description: 'Track licenses, expiration dates, and usage restrictions. Never use an expired asset.',
      },
      {
        icon: RefreshCw,
        title: 'Version Management',
        description: 'Update assets and have changes propagate everywhere. Old versions archived automatically.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Faster asset retrieval',
        description: 'Find any asset in seconds, not minutes or hours of searching.',
        stat: '95%',
      },
      {
        icon: CheckCircle,
        title: 'Brand consistency',
        description: 'Everyone uses current, approved assets. No more off-brand materials.',
        stat: '98%',
      },
      {
        icon: Users,
        title: 'Self-service access',
        description: 'Teams get what they need without bottlenecking marketing.',
        stat: '90%',
      },
      {
        icon: Shield,
        title: 'Rights compliance',
        description: 'Automatic tracking prevents use of expired or restricted assets.',
        stat: '100%',
      },
    ],
    useCases: [
      {
        title: 'Marketing Campaigns',
        description: 'Access all brand assets in one place for consistent campaign execution across channels.',
        industry: 'Marketing',
        fragment: 'assets',
      },
      {
        title: 'Partner Enablement',
        description: 'Give partners controlled access to approved assets for co-marketing activities.',
        industry: 'Business Development',
        fragment: 'roles',
      },
      {
        title: 'Document Creation',
        description: 'Insert approved logos, images, and graphics directly into templates from the DAM.',
        industry: 'Operations',
        fragment: 'taskpane',
      },
    ],
    faq: [
      {
        question: 'How do people find assets?',
        answer: 'AI-powered search with auto-tagging finds any asset by keyword, color, type or visual similarity.',
      },
      {
        question: 'What happens to old versions?',
        answer: 'Update an asset and the change propagates everywhere. Old versions are archived automatically.',
      },
      {
        question: 'Can we track licenses and usage rights?',
        answer: 'Yes. Track licenses, expiration dates and usage restrictions so nobody uses an expired asset.',
      },
      {
        question: 'Can partners get access?',
        answer: 'Give partners controlled access to approved assets for co-marketing activities.',
      },
    ],
    ctaTitle: 'Organize your brand assets',
    ctaDescription: 'See how Templatean DAM brings order to your brand library.',
  },

  excel: {
    slug: 'excel',
    layout: 'giant-word',
    icon: Table2,
    color: 'from-green-500 to-emerald-600',
    colorDark: 'from-green-400 to-emerald-500',
    metaTitle: 'Excel Intelligence | Templatean',
    metaDescription: 'AI-powered Excel assistance that writes formulas, explains logic, and catches errors. Build better spreadsheets faster.',
    label: 'Excel Intelligence',
    shortLabel: 'Excel Intelligence',
    headline: 'Excel formulas explained, written, and validated by AI',
    heroLines: ['Formulas written,', 'explained, validated.'],
    subheadline: 'Stop struggling with complex formulas. AI that writes Excel formulas from plain English, explains any formula, and catches errors before they matter.',
    heroStats: [
      { value: '80%', label: 'Faster formula creation' },
      { value: '95%', label: 'Fewer formula errors' },
      { value: '100%', label: 'Formulas documented' },
    ],
    valueProposition: {
      title: 'Excel expertise for everyone',
      description: 'Whether you\'re building financial models or simple trackers, Templatean\'s Excel Intelligence helps you write better formulas, understand existing ones, and catch errors before they cause problems.',
    },
    capabilities: [
      {
        icon: Wand2,
        title: 'Natural Language to Formula',
        description: 'Describe what you want in plain English. AI writes the formula. "Sum sales by region for Q4" → =SUMIFS(...)',
      },
      {
        icon: Brain,
        title: 'Formula Explanation',
        description: 'Select any formula and get a clear explanation of what it does and why. Never inherit mystery formulas again.',
      },
      {
        icon: FileCheck,
        title: 'Error Detection',
        description: 'AI analyzes your spreadsheet for common errors, inconsistencies, and potential issues.',
      },
      {
        icon: BarChart3,
        title: 'Auto Documentation',
        description: 'Generate documentation for your spreadsheets automatically. Every formula explained.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Faster model building',
        description: 'Build complex models in hours, not days. AI handles the formula complexity.',
        stat: '70%',
      },
      {
        icon: CheckCircle,
        title: 'Fewer errors',
        description: 'AI catches formula errors and inconsistencies before they propagate.',
        stat: '95%',
      },
      {
        icon: Users,
        title: 'Knowledge transfer',
        description: 'Anyone can understand and maintain spreadsheets with auto-generated documentation.',
        stat: '100%',
      },
      {
        icon: Shield,
        title: 'Audit confidence',
        description: 'Complete formula documentation means audit questions answered instantly.',
        stat: '90%',
      },
    ],
    useCases: [
      {
        title: 'Financial Modeling',
        description: 'Build complex financial models faster with AI-assisted formula creation and validation.',
        industry: 'Finance',
        fragment: 'sheet',
      },
      {
        title: 'Data Analysis',
        description: 'Transform raw data into insights without being an Excel expert.',
        industry: 'Operations',
        fragment: 'chart',
      },
      {
        title: 'Budget Planning',
        description: 'Create sophisticated budget models with formulas that anyone can understand.',
        industry: 'Accounting',
        fragment: 'prompt',
        demo: {
          ask: 'Sum sales by region for Q4',
          answer: '=SUMIFS(Sales[Amount], Sales[Region], A2, Sales[Quarter], "Q4")  Adds every Q4 sale whose region matches A2.',
        },
      },
    ],
    faq: [
      {
        question: 'Can it write formulas from plain English?',
        answer: 'Describe what you want and AI writes the formula. "Sum sales by region for Q4" becomes =SUMIFS(...).',
      },
      {
        question: 'Can it explain formulas we inherited?',
        answer: 'Yes. Select any formula and AI explains what it does and why, in plain English.',
      },
      {
        question: 'Does it catch errors?',
        answer: 'AI analyzes your spreadsheet for common errors, inconsistencies and potential issues before they propagate.',
      },
      {
        question: 'Can it document a workbook?',
        answer: 'Documentation is generated automatically, with every formula explained.',
      },
    ],
    ctaTitle: 'Make Excel easy',
    ctaDescription: 'See how AI transforms Excel from frustrating to effortless.',
  },

  productivity: {
    slug: 'productivity',
    layout: 'statement',
    icon: Zap,
    color: 'from-amber-500 to-orange-600',
    colorDark: 'from-amber-400 to-orange-500',
    metaTitle: 'Productivity Tools | Templatean',
    metaDescription: 'Work smarter with Office add-ins that bring AI and automation directly into Word, PowerPoint, Excel, and Outlook.',
    label: 'Productivity Tools',
    shortLabel: 'Productivity',
    headline: 'AI and automation inside the Office apps you already use',
    heroLines: ['AI inside the Office', 'apps you already use.'],
    subheadline: 'Native add-ins for Word, PowerPoint, Excel, and Outlook. Access templates, AI assistance, and brand assets without leaving your workflow.',
    heroStats: [
      { value: '4', label: 'Office integrations' },
      { value: '8+ hrs', label: 'Saved weekly per user' },
      { value: '0', label: 'Workflow disruption' },
    ],
    valueProposition: {
      title: 'Power tools that live where you work',
      description: 'No context switching. No uploading files. Templatean add-ins bring everything—templates, AI assistance, brand assets—directly into Word, PowerPoint, Excel, and Outlook.',
    },
    capabilities: [
      {
        icon: FileText,
        title: 'Word Add-in',
        description: 'Insert templates, generate content with AI, check brand compliance—all inside Word.',
      },
      {
        icon: Layers,
        title: 'PowerPoint Add-in',
        description: 'Access slide libraries, apply brand styles, generate presentation content instantly.',
      },
      {
        icon: Table2,
        title: 'Excel Add-in',
        description: 'AI formula assistance, data templates, and financial modeling tools built-in.',
      },
      {
        icon: Globe,
        title: 'Outlook Add-in',
        description: 'Email templates, AI composer, and quick responses for professional communication.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Zero learning curve',
        description: 'Work in apps your team already knows. Add-ins integrate seamlessly.',
        stat: '0 days',
      },
      {
        icon: CheckCircle,
        title: 'Consistent output',
        description: 'Same tools, same templates, same quality across every Office app.',
        stat: '100%',
      },
      {
        icon: Zap,
        title: 'Instant access',
        description: 'One click to templates, assets, and AI assistance. No app switching.',
        stat: '1 click',
      },
      {
        icon: Users,
        title: 'Team adoption',
        description: 'High adoption because tools live where people already work.',
        stat: '92%',
      },
    ],
    useCases: [
      {
        title: 'Sales Enablement',
        description: 'Sales teams create proposals, presentations, and emails without leaving their workflow.',
        industry: 'Sales',
        fragment: 'taskpane',
      },
      {
        title: 'Brand Management',
        description: 'Marketing ensures brand compliance across all Office documents organization-wide.',
        industry: 'Marketing',
        fragment: 'assets',
      },
      {
        title: 'Executive Communications',
        description: 'Leadership creates polished communications quickly with AI assistance.',
        industry: 'Executive',
        fragment: 'mail',
      },
    ],
    faq: [
      {
        question: 'Which Office apps are supported?',
        answer: 'Word, PowerPoint, Excel and Outlook, each with a native add-in.',
      },
      {
        question: 'Do people need to leave their document?',
        answer: 'No. Templates, AI assistance and brand assets are available inside the app. No context switching, no uploading files.',
      },
      {
        question: 'What does the Outlook add-in do?',
        answer: 'Email templates, an AI composer and quick responses for professional communication.',
      },
      {
        question: 'Is there a learning curve?',
        answer: 'Your team works in apps it already knows. The add-ins integrate seamlessly.',
      },
    ],
    ctaTitle: 'Try the add-ins',
    ctaDescription: 'Experience seamless productivity inside your favorite Office apps.',
  },

  security: {
    slug: 'security',
    layout: 'numbers',
    icon: Shield,
    color: 'from-slate-500 to-gray-600',
    colorDark: 'from-slate-400 to-gray-500',
    metaTitle: 'Security & Privacy | Templatean',
    metaDescription: 'Enterprise-grade security with SOC 2 Type II certification, encryption, SSO, and comprehensive compliance features.',
    label: 'Security & Privacy',
    shortLabel: 'Security',
    headline: 'Enterprise security that gives you confidence, not headaches',
    heroLines: ['Enterprise security.', 'Confidence, not headaches.'],
    subheadline: 'SOC 2 Type II certified. AES-256 encryption. SSO support. Every feature designed with security-first thinking.',
    heroStats: [
      { value: 'SOC 2', label: 'Type II certified' },
      { value: '256-bit', label: 'AES encryption' },
      { value: '99.9%', label: 'Uptime SLA' },
    ],
    valueProposition: {
      title: 'Security that scales with your requirements',
      description: 'From startups to Fortune 500s, Templatean\'s security architecture meets the most demanding compliance requirements. Your data is encrypted, access is controlled, and every action is auditable.',
    },
    capabilities: [
      {
        icon: Lock,
        title: 'Data Encryption',
        description: 'AES-256 encryption at rest, TLS 1.3 in transit. Your data is protected everywhere.',
      },
      {
        icon: Key,
        title: 'SSO & SAML',
        description: 'Single sign-on with your identity provider. SAML 2.0 and OpenID Connect support.',
      },
      {
        icon: Users,
        title: 'Role-Based Access',
        description: 'Granular permissions control who can view, edit, and approve content.',
      },
      {
        icon: FileCheck,
        title: 'Audit Trails',
        description: 'Complete logs of every action. Know who did what, when, from where.',
      },
    ],
    benefits: [
      {
        icon: Shield,
        title: 'Compliance ready',
        description: 'SOC 2 Type II, ISO 27001 roadmap, GDPR compliant. Ready for your audits.',
        stat: 'Certified',
      },
      {
        icon: Lock,
        title: 'Data protection',
        description: 'Your data is never used to train AI models without explicit consent.',
        stat: 'Guaranteed',
      },
      {
        icon: Key,
        title: 'Access control',
        description: 'Define exactly who can access what with role-based permissions.',
        stat: 'Granular',
      },
      {
        icon: Globe,
        title: 'Data residency',
        description: 'Choose where your data lives. Regional hosting options available.',
        stat: 'Flexible',
      },
    ],
    useCases: [
      {
        title: 'Financial Services',
        description: 'Meet stringent regulatory requirements with comprehensive audit trails and encryption.',
        industry: 'Finance',
        fragment: 'audit',
      },
      {
        title: 'Healthcare',
        description: 'HIPAA BAA available for healthcare organizations handling PHI.',
        industry: 'Healthcare',
        fragment: 'roles',
      },
      {
        title: 'Legal',
        description: 'Client confidentiality protected with role-based access and encryption.',
        industry: 'Legal',
        fragment: 'locked',
      },
    ],
    faq: [
      {
        question: 'Is Templatean SOC 2 certified?',
        answer: 'Yes. SOC 2 Type II certified and GDPR compliant, with ISO 27001 on the roadmap.',
      },
      {
        question: 'How is data encrypted?',
        answer: 'AES-256 encryption at rest and TLS 1.3 in transit.',
      },
      {
        question: 'Do you support SSO?',
        answer: 'Yes. Single sign-on with your identity provider, with SAML 2.0 and OpenID Connect support.',
      },
      {
        question: 'Is our data used to train AI models?',
        answer: 'Never without your explicit consent.',
      },
      {
        question: 'Is a HIPAA BAA available?',
        answer: 'Yes, for healthcare organizations handling PHI.',
      },
    ],
    ctaTitle: 'Review our security practices',
    ctaDescription: 'Download our security whitepaper and SOC 2 report summary.',
  },

  integrations: {
    slug: 'integrations',
    layout: 'sticky-split',
    icon: Plug,
    color: 'from-indigo-500 to-blue-600',
    colorDark: 'from-indigo-400 to-blue-500',
    metaTitle: 'Integrations | Templatean',
    metaDescription: 'Templatean connects to Word, Excel, PowerPoint and Outlook, HubSpot, SharePoint, Excel Online, SQL databases and custom REST APIs, with Slack notifications and a REST API for custom builds.',
    label: 'Integrations',
    shortLabel: 'Integrations',
    headline: 'Connect the systems you already use',
    heroLines: ['Connect your stack.', 'Documents fill themselves.'],
    subheadline: 'Add-ins for Word, Excel, PowerPoint and Outlook. HubSpot for CRM data. Connectors for SharePoint, Excel Online, SQL databases and custom REST APIs.',
    heroStats: [
      { value: '4', label: 'Office add-ins' },
      { value: '4', label: 'Data connector types' },
      { value: '3', label: 'AI model providers' },
    ],
    valueProposition: {
      title: 'Your systems, connected',
      description: 'Stop copying data between systems. Templatean fills template fields from HubSpot, SharePoint, Excel Online, SQL databases and your own REST APIs, inside the Office apps your team already works in.',
    },
    capabilities: [
      {
        icon: Layers,
        title: 'Microsoft 365 Add-ins',
        description: 'Native add-ins for Word, Excel, PowerPoint and Outlook bring templates, AI and brand assets into the document.',
      },
      {
        icon: Users,
        title: 'HubSpot CRM',
        description: 'Search HubSpot contacts, companies and deals, and pull their data into proposals and contracts.',
      },
      {
        icon: Database,
        title: 'Data Connectors',
        description: 'Use SharePoint, Excel Online, a SQL database or a custom REST API as the source for template fields.',
      },
      {
        icon: MessageSquare,
        title: 'Slack Notifications',
        description: 'Send notifications and updates to a Slack channel.',
      },
      {
        icon: Brain,
        title: 'AI Model Providers',
        description: 'Connect OpenRouter, OpenAI or Anthropic for AI content generation.',
      },
      {
        icon: Share2,
        title: 'REST API',
        description: 'A REST API for custom builds and integrations of your own.',
      },
    ],
    benefits: [
      {
        icon: Clock,
        title: 'Less copy and paste',
        description: 'Fields fill from HubSpot or a connected data source instead of being retyped by hand.',
        stat: 'Auto-fill',
      },
      {
        icon: CheckCircle,
        title: 'Data from the source',
        description: 'Figures come from the system that owns them, not from a copy someone pasted last month.',
        stat: 'At source',
      },
      {
        icon: Layers,
        title: 'Inside Office',
        description: 'Add-ins for Word, Excel, PowerPoint and Outlook, so nobody leaves the document to find data.',
        stat: '4 add-ins',
      },
      {
        icon: Settings,
        title: 'Room to build',
        description: 'The REST API and custom REST connectors cover systems without a ready-made connector.',
        stat: 'REST API',
      },
    ],
    useCases: [
      {
        title: 'Proposals from HubSpot',
        description: 'Fill proposals and contracts with the contact, company and deal data already in HubSpot.',
        industry: 'Sales',
        fragment: 'form',
      },
      {
        title: 'Reports from your data',
        description: 'Fill reports from a SharePoint list, an Excel Online workbook or a SQL database.',
        industry: 'Operations',
        fragment: 'sheet',
      },
      {
        title: 'HR documents through a connector',
        description: 'No ready-made HR integration: offer letters and onboarding documents fill from your HR data through a custom REST or SQL database connector.',
        industry: 'Human Resources',
        fragment: 'form',
      },
    ],
    faq: [
      {
        question: 'Which CRM do you connect to?',
        answer: 'HubSpot. Contacts, companies and deals can fill proposals and contracts. For other systems, use a custom REST API or SQL database connector.',
      },
      {
        question: 'Which data sources can fill a template?',
        answer: 'SharePoint, Excel Online, SQL databases and custom REST APIs, plus HubSpot for CRM data.',
      },
      {
        question: 'Does it work inside Microsoft 365?',
        answer: 'Yes. There are add-ins for Word, Excel, PowerPoint and Outlook.',
      },
      {
        question: 'Which AI models can we use?',
        answer: 'Templatean connects to OpenRouter, OpenAI and Anthropic.',
      },
      {
        question: 'Can we get notifications in Slack?',
        answer: 'Yes. Templatean can send notifications and updates to a Slack channel.',
      },
      {
        question: 'Is there an API?',
        answer: 'Yes. A REST API for custom builds and integrations of your own.',
      },
    ],
    ctaTitle: 'Explore integrations',
    ctaDescription: 'See all available integrations and how to connect your tools.',
  },
  analytics: {
    slug: 'analytics',
    layout: 'heatmap',
    icon: BarChart3,
    color: 'from-blue-500 to-indigo-600',
    colorDark: 'from-blue-400 to-indigo-500',
    metaTitle: 'Usage Analytics | Templatean',
    metaDescription: 'See which templates and assets your teams use, who uses them, and where documents stall. Usage trends, heatmaps, field completion and exports.',
    label: 'Usage Analytics',
    shortLabel: 'Analytics',
    headline: 'See which templates work, and where documents stall',
    heroLines: ['Every template, measured.', 'Every document, traced.'],
    subheadline: 'Usage analytics and audit trails that connect every document to a real outcome: which templates get used, by whom, and which fields people skip.',
    heroStats: [
      { value: '6', label: 'Metrics on every template' },
      { value: '24×7', label: 'Hour-by-day usage heatmap' },
      { value: 'CSV', label: 'And JSON export' },
    ],
    valueProposition: {
      title: 'Know what your templates are doing',
      description: 'Templatean records every view, download and generated document. Admins see adoption across the organization; template owners see exactly how their template is used and where people get stuck.',
    },
    capabilities: [
      {
        icon: Activity,
        title: 'Usage Trends',
        description: 'Documents generated, active users and AI requests over any date range, compared with the period before.',
      },
      {
        icon: CalendarClock,
        title: 'Usage Heatmap',
        description: 'Activity by hour and day of the week, so you can see when teams actually produce documents.',
      },
      {
        icon: ListChecks,
        title: 'Field Completion',
        description: 'Completion rate for every template field. Spot the fields people skip and fix the template.',
      },
      {
        icon: Users,
        title: 'Teams and People',
        description: 'Breakdown by department, plus the most active users and the most used templates and assets.',
      },
    ],
    benefits: [
      {
        icon: BarChart3,
        title: 'Template performance',
        description: 'Views, downloads, documents generated, unique users, average duration and field completion for each template.',
        stat: 'Per template',
      },
      {
        icon: FileCheck,
        title: 'Activity log',
        description: 'A record of who did what and when, across templates, assets and documents.',
        stat: 'Every action',
      },
      {
        icon: Download,
        title: 'Your data, exported',
        description: 'Export the filtered analytics as CSV or JSON for your own reporting.',
        stat: 'CSV · JSON',
      },
      {
        icon: Lock,
        title: 'Permission-based',
        description: 'The organization-wide dashboard is limited to people with the analytics permission.',
        stat: 'Role-based',
      },
    ],
    useCases: [
      {
        title: 'Template adoption',
        description: 'See which templates teams use and which they ignore, then retire or rework the rest.',
        industry: 'Brand',
        fragment: 'chart',
      },
      {
        title: 'Fixing slow templates',
        description: 'Low field completion and long average durations point to the fields that slow people down.',
        industry: 'Operations',
        fragment: 'form',
      },
      {
        title: 'Audit and review',
        description: 'The activity log shows who generated, edited or downloaded a document, and when.',
        industry: 'Compliance',
        fragment: 'audit',
      },
    ],
    faq: [
      {
        question: 'What does Templatean measure?',
        answer: 'Views, downloads and generated documents for every template, plus active users, AI requests, assets used and field completion.',
      },
      {
        question: 'Who can see analytics?',
        answer: 'The organization-wide dashboard needs the analytics permission. Each template\'s own analytics open from its template page.',
      },
      {
        question: 'Can we export the data?',
        answer: 'Yes. Export the filtered analytics as CSV or JSON.',
      },
      {
        question: 'Which plans include analytics?',
        answer: 'The analytics dashboard is included from the Professional plan.',
      },
    ],
    ctaTitle: 'See your templates at work',
    ctaDescription: 'Start a free trial and watch usage build from the first document.',
  },
}

export const platformFeaturesList = Object.values(platformFeatures)
